import { HttpClient } from '../api/HttpClient';
import { API_ENDPOINTS } from '../api/ApiConfig';
import type {
  MusicVideo,
  MusicVideosData,
  MusicVideosQueryParams,
  CreateMusicVideoRequest,
  UpdateMusicVideoRequest,
} from '../../types/api/music-video';

export class MusicVideoService {
  private httpClient = HttpClient.getInstance();

  async getMusicVideos(params: MusicVideosQueryParams = {}): Promise<MusicVideosData> {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.append('page', String(params.page));
    if (params.size !== undefined) query.append('size', String(params.size));
    if (params.keyword) query.append('keyword', params.keyword);
    if (params.albumId) query.append('albumId', params.albumId);
    const url = `${API_ENDPOINTS.MUSIC_VIDEOS.ADMIN_LIST}?${query}`;
    const response = await this.httpClient.get<MusicVideosData>(url);
    if (!response.success) throw new Error(response.message || 'Failed to fetch music videos');
    return response.data;
  }

  async getMusicVideo(musicVideoId: string): Promise<MusicVideo> {
    const response = await this.httpClient.get<MusicVideo>(
      API_ENDPOINTS.MUSIC_VIDEOS.ADMIN_DETAIL(musicVideoId)
    );
    if (!response.success) throw new Error(response.message || 'Failed to fetch music video');
    return response.data;
  }

  /**
   * Create a music video. Sends multipart/form-data: `data` (JSON) + optional `thumbnail` + optional `video`.
   */
  async createMusicVideo(
    data: CreateMusicVideoRequest,
    thumbnailFile?: File,
    videoFile?: File
  ): Promise<MusicVideo> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    formData.append('data', jsonBlob, 'data.json');
    if (thumbnailFile) {
      formData.append('thumbnail', thumbnailFile, thumbnailFile.name);
    }
    if (videoFile) {
      formData.append('video', videoFile, videoFile.name);
    }

    const response = await this.httpClient.request<MusicVideo>({
      method: 'POST',
      url: API_ENDPOINTS.MUSIC_VIDEOS.ADMIN_CREATE,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (!response.success) throw new Error(response.message || 'Failed to create music video');
    return response.data;
  }

  /**
   * Update a music video. Sends multipart/form-data: `data` (JSON) + optional replacement files.
   */
  async updateMusicVideo(
    musicVideoId: string,
    data: UpdateMusicVideoRequest,
    thumbnailFile?: File,
    videoFile?: File
  ): Promise<MusicVideo> {
    const formData = new FormData();
    const jsonBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    formData.append('data', jsonBlob, 'data.json');
    if (thumbnailFile) {
      formData.append('thumbnail', thumbnailFile, thumbnailFile.name);
    }
    if (videoFile) {
      formData.append('video', videoFile, videoFile.name);
    }

    const response = await this.httpClient.request<MusicVideo>({
      method: 'PUT',
      url: API_ENDPOINTS.MUSIC_VIDEOS.ADMIN_UPDATE(musicVideoId),
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (!response.success) throw new Error(response.message || 'Failed to update music video');
    return response.data;
  }

  async deleteMusicVideo(musicVideoId: string): Promise<void> {
    const response = await this.httpClient.delete<{ message: string }>(
      API_ENDPOINTS.MUSIC_VIDEOS.ADMIN_DELETE(musicVideoId)
    );
    if (!response.success) throw new Error(response.message || 'Failed to delete music video');
  }
}
